import { Component, inject, signal, output } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Input } from '@arm-web/ui-components';
import { EncryptionService } from '../../../services/encryption.service';
import { InlineAlertComponent } from '../../inline-alert/inline-alert';
import {
  ActionButtonsComponent,
  asActionButtonVariant,
} from '../../action-buttons/action-buttons';

@Component({
  selector: 'app-generate-key-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    Input,
    InlineAlertComponent,
    ActionButtonsComponent,
  ],
  templateUrl: './generate-key-form.html',
  styleUrl: './generate-key-form.css',
})
export class GenerateKeyFormComponent {
  private readonly encryptionService = inject(EncryptionService);

  // Reactive form
  generateKeyForm = new FormGroup({
    currentKey: new FormControl('', [Validators.required]),
  });

  // Component signals
  isGenerating = signal<boolean>(false);
  isSubmitting = signal<boolean>(false);
  errorMessage = signal<string>('');
  generatedKey = signal<string>('');
  isKeyGenerated = signal<boolean>(false);

  // Output events
  keyGenerated = output<void>();
  cancelled = output<void>();

  /**
   * Handles generating a new encryption key
   */
  handleGenerateKey(): void {
    // Reset error message
    this.errorMessage.set('');

    // Validate form
    if (this.generateKeyForm.invalid) {
      this.errorMessage.set('Please enter your current encryption key');
      this.generateKeyForm.markAllAsTouched();
      return;
    }

    // Get form value
    const currentKey = this.generateKeyForm.get('currentKey')?.value?.trim();
    if (!currentKey) {
      this.errorMessage.set('Please enter your current encryption key');
      return;
    }

    // Set generating state
    this.isGenerating.set(true);

    // First validate the current key matches the backend
    this.encryptionService.validateCurrentKey(currentKey).subscribe({
      next: () => {
        // Key validation successful, now generate new key
        this.encryptionService.generateNewKey().subscribe({
          next: response => {
            this.isGenerating.set(false);
            this.generatedKey.set(response.key);
            this.isKeyGenerated.set(true);

            // Auto-download the key
            this.encryptionService.downloadKey(response.key);

            // Show helper message
            this.errorMessage.set('');
          },
          error: error => {
            this.isGenerating.set(false);
            this.errorMessage.set(
              error.message || 'Failed to generate key. Please try again.'
            );
          },
        });
      },
      error: (error: Error) => {
        this.isGenerating.set(false);
        this.errorMessage.set(
          error.message ||
            'Current key does not match. Please enter the correct key.'
        );
      },
    });
  }

  /**
   * Handles applying the new encryption key
   */
  handleSubmit(): void {
    if (!this.generatedKey()) {
      this.errorMessage.set('Please generate a new key first');
      return;
    }

    const currentKey = this.generateKeyForm.get('currentKey')?.value?.trim();
    if (!currentKey) {
      this.errorMessage.set('Current key is required');
      return;
    }

    // Set submitting state
    this.isSubmitting.set(true);

    // Apply new key
    this.encryptionService
      .applyNewKey(currentKey, this.generatedKey())
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);

          // Reset form
          this.generateKeyForm.reset();
          this.generatedKey.set('');
          this.isKeyGenerated.set(false);

          // Emit success event (offcanvas will close)
          this.keyGenerated.emit();
        },
        error: error => {
          this.isSubmitting.set(false);
          this.errorMessage.set(
            error.message ||
              'Failed to apply new key. Please try again or contact support.'
          );
        },
      });
  }

  /**
   * Handles close button click
   */
  handleClose(): void {
    this.cancelled.emit();
  }

  /**
   * Handle action button clicks
   */
  onActionButtonClick(buttonId: string): void {
    switch (buttonId) {
      case 'generate-key-btn':
        this.handleGenerateKey();
        break;
      case 'submit-key-btn':
        this.handleSubmit();
        break;
      case 'close-btn':
        this.handleClose();
        break;
    }
  }

  asActionButtonVariant = asActionButtonVariant;
}
